import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, RefreshCw, Paperclip, FileDown } from 'lucide-react';
import { staffClient } from 'clients/staff';
import { categoryApi } from 'apis/category';
import Button from 'ui/Button';
import { TextField, SelectField, ToggleGroup, ReadonlyField } from 'ui/form';
import SearchSelect from 'ui/SearchSelect';
import Table, { Column } from 'ui/Table';
import Modal from 'ui/Modal';
import CategoryManager, { CategoryItem } from 'ui/CategoryManager';
import Loading from 'ui/Loading';
import Tabs, { Tab } from 'ui/Tabs';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import Section from 'ui/Section';
import TableContainer from 'ui/TableContainer';
import { Badge } from 'ui/shadcn';
import {
    SiteCategory,
    SupervisionDateSource,
    SITE_CATEGORY_OPTIONS,
} from 'apis/site';
import { cn } from 'lib/utils';
import MultiFileUploader from 'components/Form/Input/File/MultiUploader';
import { getS3UploadUrl, uploadS3File } from 'utils/s3';
import { FileUtils } from 'components/Utils/file';

interface Site {
    id: number;
    name: string;
    location: string;
    type: string;
    /** 현장 구분 (감리/공사/설계) */
    category?: SiteCategory;
    constructionPeriod: string; // deprecated - 하위 호환성 유지
    constructionStartDate?: string;
    constructionEndDate?: string;
    totalFloorArea: string;
    buildingScale: string;
    numberOfUnits: string;
    supervisionPeriod: string; // deprecated - 하위 호환성 유지
    /** 현장정보에서 직접 입력하는 감리 일정 (서버 원본 필드) */
    supervisionReportDate?: string | null;   // 감리 시작신고일
    supervisionDeployDate?: string | null;   // 감리원 투입일
    supervisionFinishDate?: string | null;   // 감리 종료일
    /** 조회 전용 파생값 — 입력값이 있으면 그 값, 없으면 감리원 배치에서 계산 */
    supervisionStartDate?: string;
    supervisionEndDate?: string;
    supervisionDateSource?: SupervisionDateSource;
    chiefSupervisor: string;
    contractorName: string;
    workTypes?: string[];
    progressRate: string;
    visible: boolean;
}

interface Board {
    id: number;
    name: string;
    board_type: 'general' | 'document';
    document_category?: string;
    categories?: Category[];
}

interface Category {
    id: number;
    name: string;
    order: number;
    createdAt?: string;
}

interface Staff {
    id: number;
    name: string;
    user_id: string;
    user_type: string;
    position?: string;
}

interface Assignment {
    id: number;
    staff_id: number;
    site_id: number;
    position: 'CHIEF' | 'ASSISTANT' | 'NON_RESIDENT' | 'ETC';
    work_types: string[];
    start_date: string;
    end_date?: string;
    is_active: boolean;
    staff?: Staff;
}

interface ContractFile {
    id?: number;
    name: string;
    url: string;
    type: string;
    size: number;
    hash: string;
}

interface Contract {
    id: number;
    contractRevision: number;
    contractDate: string;
    status?: string;
    workTypes?: string[];
    businessApprovalDate?: string;
    biddingNoticeDate?: string;
    remarks?: string;
    siteId: number;
    files?: ContractFile[];
}

// 파일 툴팁 컴포넌트
const FileTooltip: React.FC<{ files: ContractFile[] }> = ({ files }) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, triggerHeight: 0 });
    const triggerRef = useRef<HTMLSpanElement>(null);

    const handleMouseEnter = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPosition({
                top: rect.top,
                left: rect.left + rect.width / 2,
                triggerHeight: rect.height
            });
        }
        setShow(true);
    };

    const tooltip = show ? ReactDOM.createPortal(
        <div
            className="fixed z-[9999]"
            style={{
                top: position.top,
                left: position.left,
                transform: 'translate(-50%, -100%)'
            }}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {/* 툴팁 본체 */}
            <div className="min-w-[220px] max-w-[320px] rounded border border-border bg-popover p-2 text-popover-foreground">
                <div className="mb-1 border-b border-border px-2 pb-2 text-xs font-semibold text-neutral-700">
                    첨부파일 <span className="tabular-nums text-muted-foreground">({files.length})</span>
                </div>
                <div className="max-h-[200px] space-y-0.5 overflow-y-auto">
                    {files.map((file, idx) => (
                        <div
                            key={idx}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-neutral-700 transition-colors duration-fast hover:bg-navy-50 hover:text-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                FileUtils.downloadFile(file.url, file.name);
                            }}
                        >
                            <FileDown className="h-4 w-4 shrink-0 text-neutral-400" />
                            <span className="flex-1 truncate text-xs">{file.name}</span>
                        </div>
                    ))}
                </div>
            </div>
            {/* 브릿지: 트리거와 툴팁 연결 */}
            <div
                className="w-full"
                style={{ height: position.triggerHeight + 4 }}
            />
        </div>,
        document.body
    ) : null;

    return (
        <>
            <span
                ref={triggerRef}
                className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-navy-50 px-2 py-0.5 text-xs font-medium tabular-nums text-primary transition-colors duration-fast hover:bg-navy-100"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setShow(false)}
            >
                <Paperclip className="h-3 w-3" />
                {files.length}
            </span>
            {tooltip}
        </>
    );
};

// 공종 뱃지 — Home 대시보드와 같은 톤 매핑 (계약은 통신을 NETWORK로 저장)
const WORK_TYPE_BADGES: Record<string, { label: string; tone: 'warning' | 'danger' | 'info' }> = {
    ELECTRIC: { label: '전기', tone: 'warning' },
    FIRE: { label: '소방', tone: 'danger' },
    TELECOM: { label: '통신', tone: 'info' },
    NETWORK: { label: '통신', tone: 'info' },
};

const WorkTypeBadges: React.FC<{ types?: string[] }> = ({ types }) => {
    if (!types || types.length === 0) return <span className="text-neutral-400">-</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {types.map((t) => {
                const config = WORK_TYPE_BADGES[t];
                return (
                    <Badge key={t} variant={config?.tone || 'secondary'}>
                        {config?.label || t}
                    </Badge>
                );
            })}
        </div>
    );
};

// 현장 workTypes 선택지 (계약의 workTypes와는 별개 — 이건 현장 자체의 공종)
const SITE_WORK_TYPE_OPTIONS = [
    { value: 'ELECTRIC', label: '전기' },
    { value: 'FIRE', label: '소방' },
    { value: 'TELECOM', label: '통신' }
];

const formatDate = (value?: string) =>
    value ? (
        <span className="tabular-nums">{new Date(value).toLocaleDateString()}</span>
    ) : (
        <span className="text-neutral-400">-</span>
    );

const formatDateText = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString() : null;

const FORM_GRID = 'grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2';


const SiteEdit: React.FC = () => {
    const { siteId } = useParams<{ siteId: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    
    // 문서 카테고리 한글 매핑
    const documentCategories: { [key: string]: string } = {
        'general_mgmt': '일반관리',
        'design_mgmt': '설계관리',
        'process_mgmt': '공정관리',
        'construction_mgmt': '시공관리',
        'completion_mgmt': '준공관리'
    };
    
    // Site data
    const [site, setSite] = useState<Site | null>(null);
    const [formData, setFormData] = useState<Partial<Site>>({});
    
    // Boards and Categories
    const [boards, setBoards] = useState<Board[]>([]);
    const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
    const [boardCategories, setBoardCategories] = useState<CategoryItem[]>([]);
    const [savingCategories, setSavingCategories] = useState(false);
    
    // Assignments (배치인원)
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [staffList, setStaffList] = useState<Staff[]>([]);
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [assignmentForm, setAssignmentForm] = useState<Partial<Assignment>>({
        position: 'CHIEF',
        work_types: [],
        staff_id: 0,
        start_date: '',
        end_date: '',
        is_active: true
    });
    const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
    
    // Contracts
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [showContractModal, setShowContractModal] = useState(false);
    const [contractForm, setContractForm] = useState<Partial<Contract>>({
        contractRevision: 1,
        contractDate: '',
        status: '',
        workTypes: [],
        businessApprovalDate: '',
        biddingNoticeDate: '',
        remarks: ''
    });
    const [contractFiles, setContractFiles] = useState<any[]>([]);
    const [contractSaving, setContractSaving] = useState(false);
    const [editingContract, setEditingContract] = useState<Contract | null>(null);
    
    
    // Documents
    const [documentBoards, setDocumentBoards] = useState<Board[]>([]);
    const [documentCounts, setDocumentCounts] = useState<{ [key: string]: number }>({});


    useEffect(() => {
        if (siteId) {
            fetchData();
        }
    }, [siteId]);

    const fetchData = async () => {
        const siteData = await fetchSiteData();
        if (siteData) {
            await fetchBoards(siteData);
            await fetchDocumentBoards(siteData);
        }
        await fetchAssignments();
        await fetchStaffList();
        await fetchContracts();
    };

    const fetchSiteData = async () => {
        try {
            const response = await staffClient.get(`/site/${siteId}`);
            setSite(response.data);
            setFormData(response.data);
            return response.data; // 데이터 반환
        } catch (error) {
            console.error('Failed to fetch site:', error);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const fetchBoards = async (siteData: any) => {
        console.log('[fetchBoards] Site data:', siteData);
        
        try {
            // 1. board_id로 직접 조회 시도
            if (siteData.board_id) {
                console.log('[fetchBoards] Trying with board_id:', siteData.board_id);
                try {
                    const boardResponse = await staffClient.get(`/board/${siteData.board_id}`);
                    const board = boardResponse.data;
                    console.log('[fetchBoards] Board found by board_id:', board);
                    
                    // 카테고리 정보도 함께 가져오기
                    try {
                        const categoryResponse = await staffClient.get(`/category?boardId=${siteData.board_id}`);
                        board.categories = Array.isArray(categoryResponse.data) ? categoryResponse.data : [];
                        console.log('[fetchBoards] Categories loaded:', board.categories);
                    } catch (catError) {
                        console.log('[fetchBoards] Categories fetch failed:', catError);
                        board.categories = [];
                    }
                    
                    setBoards([board]);
                    handleSelectBoard(board);
                    return;
                } catch (boardError) {
                    console.log('[fetchBoards] Failed to fetch by board_id:', boardError);
                }
            }

            // 2. boards 배열 확인 (현장이 여러 게시판을 가진 경우)
            if (siteData.boards && Array.isArray(siteData.boards) && siteData.boards.length > 0) {
                console.log('[fetchBoards] Site has boards array:', siteData.boards);
                
                // 일반 게시판 찾기 (board_type === 'general')
                const siteBoard = siteData.boards.find((b: any) => b.board_type === 'general') || siteData.boards[0];
                console.log('[fetchBoards] Selected board:', siteBoard);
                
                // 이미 categories가 포함되어 있는지 확인
                if (siteBoard.categories && Array.isArray(siteBoard.categories)) {
                    console.log('[fetchBoards] Board already has categories:', siteBoard.categories);
                    setBoards([siteBoard]);
                    handleSelectBoard(siteBoard);
                    return;
                }
                
                // 카테고리가 없으면 별도로 가져오기
                try {
                    const categoryResponse = await categoryApi.getByBoardId(siteBoard.id);
                    siteBoard.categories = Array.isArray(categoryResponse) ? categoryResponse : [];
                    console.log('[fetchBoards] Fetched categories:', siteBoard.categories);
                } catch (catError) {
                    console.log('[fetchBoards] Categories fetch failed, using empty array:', catError);
                    siteBoard.categories = [];
                }
                
                setBoards([siteBoard]);
                handleSelectBoard(siteBoard);
                return;
            }

            // 3. 전체 게시판에서 현장과 연결된 것 찾기
            console.log('[fetchBoards] Fetching all boards to find site board');
            const boardsResponse = await staffClient.get('/board');
            const allBoards = Array.isArray(boardsResponse.data) ? boardsResponse.data : [];
            console.log('[fetchBoards] All boards:', allBoards);
            
            // 여러 조건으로 매칭 시도
            const siteBoard = allBoards.find((board: any) => {
                // site_id로 매칭
                if (board.site_id === siteData.id) {
                    console.log('[fetchBoards] Matched by site_id');
                    return true;
                }
                // siteId로 매칭 (필드명이 다를 수 있음)
                if (board.siteId === siteData.id) {
                    console.log('[fetchBoards] Matched by siteId');
                    return true;
                }
                // 이름으로 매칭
                if (board.name === siteData.name) {
                    console.log('[fetchBoards] Matched by exact name');
                    return true;
                }
                // 이름 포함으로 매칭
                if (siteData.name && board.name && board.name.includes(siteData.name)) {
                    console.log('[fetchBoards] Matched by name inclusion');
                    return true;
                }
                return false;
            });
            
            if (siteBoard) {
                console.log('[fetchBoards] Found matching board:', siteBoard);
                
                // 카테고리 정보 가져오기
                try {
                    const categoryResponse = await staffClient.get(`/category?boardId=${siteBoard.id}`);
                    siteBoard.categories = Array.isArray(categoryResponse.data) ? categoryResponse.data : [];
                    console.log('[fetchBoards] Categories loaded for matched board:', siteBoard.categories);
                } catch (catError) {
                    console.log('[fetchBoards] Categories fetch failed:', catError);
                    siteBoard.categories = [];
                }
                
                setBoards([siteBoard]);
                handleSelectBoard(siteBoard);
            } else {
                console.log('[fetchBoards] No matching board found for site');
                setBoards([]);
                setSelectedBoard(null);
                setBoardCategories([]);
            }
        } catch (error) {
            console.error('[fetchBoards] Error:', error);
            setBoards([]);
            setSelectedBoard(null);
            setBoardCategories([]);
        }
    };

    const handleSelectBoard = (board: Board) => {
        console.log('[handleSelectBoard] Selecting board:', board);
        setSelectedBoard(board);
        
        // 카테고리 데이터 형식 변환 - 새로운 배열 생성으로 React가 변경을 감지하도록 함
        const boardCats = (board.categories || [])
            .sort((a, b) => a.order - b.order)
            .map(cat => ({
                id: cat.id.toString(),
                name: cat.name,
                order: cat.order
            }));
        console.log('[handleSelectBoard] Setting categories:', boardCats);
        setBoardCategories([...boardCats]); // 새로운 배열 참조 생성
    };

    const fetchDocumentBoards = async (siteData: any) => {
        console.log('[fetchDocumentBoards] Fetching document boards for site:', siteData);
        
        try {
            if (siteData.boards && Array.isArray(siteData.boards)) {
                // 문서 게시판들만 필터링 (board_type === 'document')
                const docBoards = siteData.boards.filter((b: any) => b.board_type === 'document');
                console.log('[fetchDocumentBoards] Document boards:', docBoards);
                
                setDocumentBoards(docBoards);
                
                // 서버의 일괄 API 사용하여 문서 개수 가져오기
                const counts: { [key: string]: number } = {};
                
                for (const board of docBoards) {
                    try {
                        // 서버의 board/{boardId}/document-counts API 사용
                        const response = await staffClient.get(`/article/board/${board.id}/document-counts`);
                        
                        if (response.data) {
                            // response.data가 카테고리별 개수를 담은 객체인 경우
                            Object.entries(response.data).forEach(([categoryId, count]) => {
                                counts[`${board.id}-${categoryId}`] = count as number;
                            });
                            console.log(`[fetchDocumentBoards] Board ${board.id} document counts:`, response.data);
                        }
                    } catch (error) {
                        console.log(`Failed to fetch document counts for board ${board.id}:`, error);
                        // 에러 시 각 카테고리를 0으로 초기화
                        const categories = board.categories || [];
                        categories.forEach((category: any) => {
                            counts[`${board.id}-${category.id}`] = 0;
                        });
                    }
                }
                
                setDocumentCounts(counts);
                console.log('[fetchDocumentBoards] Final document counts:', counts);
            }
        } catch (error) {
            console.error('[fetchDocumentBoards] Error:', error);
            setDocumentBoards([]);
            setDocumentCounts({});
        }
    };

    const fetchAssignments = async () => {
        try {
            const response = await staffClient.get(`/staffs/assignments?siteId=${siteId}`);
            setAssignments(response.data);
        } catch (error) {
            console.error('Failed to fetch assignments:', error);
            setAssignments([]);
        }
    };

    const fetchStaffList = async () => {
        try {
            const response = await staffClient.get('/staffs?limit=1000');
            const staffData = response.data?.data || response.data || [];
            // 관리자(MASTER) 제외, 일반 직원(STAFF)만 표시
            const filteredStaff = staffData.filter((staff: Staff) => staff.user_type !== 'MASTER');
            setStaffList(filteredStaff);
        } catch (error) {
            console.error('Failed to fetch staff list:', error);
            setStaffList([]);
        }
    };


    const handleSiteUpdate = async () => {
        try {
            // 조회 전용 파생값·관계는 보내지 않는다 (서버가 계산/조립하는 값)
            const {
                supervisionStartDate: _start,
                supervisionEndDate: _end,
                supervisionDateSource: _source,
                id: _id,
                ...rest
            } = formData as Partial<Site> & Record<string, any>;
            delete rest.boards;
            delete rest.created_at;
            delete rest.updated_at;

            const payload = {
                ...rest,
                // 비우면 null로 보내 "직접 입력 해제" → 다시 감리원 배치 계산값을 쓰게 한다
                supervisionReportDate: formData.supervisionReportDate || null,
                supervisionDeployDate: formData.supervisionDeployDate || null,
                supervisionFinishDate: formData.supervisionFinishDate || null,
            };

            await staffClient.patch(`/site/${siteId}`, payload);
            alert('현장 정보가 수정되었습니다.');
            navigate('/admin/site-management');
        } catch (error) {
            console.error('Failed to update site:', error);
            alert('수정 중 오류가 발생했습니다.');
        }
    };

    // Category handlers
    const handleCategoriesChange = (categories: CategoryItem[]) => {
        setBoardCategories(categories);
    };

    const handleCategoriesSave = async () => {
        if (!selectedBoard) return;

        // 변경사항 체크
        const existingCategories = selectedBoard.categories || [];
        const existingIds = existingCategories.map(cat => cat.id.toString());
        const currentIds = boardCategories.filter(cat => cat.id && !cat.id.startsWith('new-')).map(cat => cat.id);

        const toDelete = existingIds.filter(id => !currentIds.includes(id));
        const toCreate = boardCategories.filter(cat => cat.id.startsWith('new-'));
        const toUpdate = boardCategories.filter(cat => !cat.id.startsWith('new-'));

        // 이름이나 순서가 변경된 카테고리 찾기
        const hasUpdates = toUpdate.some(cat => {
            const existingCat = existingCategories.find(c => c.id.toString() === cat.id);
            const newOrder = boardCategories.indexOf(cat) + 1;
            return existingCat && (existingCat.name !== cat.name || existingCat.order !== newOrder);
        });

        // 변경사항이 없으면 안내 메시지
        if (toDelete.length === 0 && toCreate.length === 0 && !hasUpdates) {
            alert('변경된 카테고리가 없습니다.');
            return;
        }

        setSavingCategories(true);
        try {
            // 삭제할 카테고리
            for (const id of toDelete) {
                await categoryApi.delete(parseInt(id));
            }

            // 새로 추가할 카테고리
            for (let i = 0; i < toCreate.length; i++) {
                await categoryApi.create({
                    name: toCreate[i].name,
                    boardId: selectedBoard.id,
                    order: boardCategories.indexOf(toCreate[i]) + 1
                });
            }

            // 기존 카테고리 이름 및 순서 업데이트
            for (const cat of toUpdate) {
                const existingCat = existingCategories.find(c => c.id.toString() === cat.id);
                const newOrder = boardCategories.indexOf(cat) + 1;

                if (existingCat && (existingCat.name !== cat.name || existingCat.order !== newOrder)) {
                    await categoryApi.update(parseInt(cat.id), {
                        name: cat.name,
                        order: newOrder
                    });
                }
            }

            alert('카테고리가 저장되었습니다.');

            // 사이트 정보를 다시 가져와서 최신 데이터로 갱신
            const siteResponse = await staffClient.get(`/site/${siteId}`);
            const updatedSiteData = siteResponse.data;
            setSite(updatedSiteData);
            setFormData(updatedSiteData);

            // 보드 정보도 다시 가져오기
            await fetchBoards(updatedSiteData);
        } catch (error) {
            console.error('Failed to save categories:', error);
            alert('카테고리 저장 중 오류가 발생했습니다.');
        } finally {
            setSavingCategories(false);
        }
    };

    // Assignment handlers
    const handleAssignmentAdd = () => {
        setAssignmentForm({
            position: 'CHIEF',
            work_types: [],
            staff_id: 0,
            start_date: '',
            end_date: '',
            is_active: true
        });
        setEditingAssignment(null);
        setShowAssignmentModal(true);
    };

    const handleAssignmentEdit = (assignment: Assignment) => {
        setAssignmentForm({
            position: assignment.position,
            work_types: assignment.work_types || [],
            staff_id: assignment.staff_id,
            start_date: assignment.start_date,
            end_date: assignment.end_date || '',
            is_active: assignment.is_active
        });
        setEditingAssignment(assignment);
        setShowAssignmentModal(true);
    };

    const handleAssignmentSave = async () => {
        if (!assignmentForm.staff_id) {
            toast.error('직원을 선택해주세요.');
            return;
        }

        try {
            const data = {
                ...assignmentForm,
                site_id: Number(siteId)
            };

            if (editingAssignment) {
                await staffClient.patch(`/staffs/assignments/${editingAssignment.id}`, data);
                toast.success('감리원이 수정되었습니다.');
            } else {
                await staffClient.post(`/staffs/${assignmentForm.staff_id}/assignments`, data);
                toast.success('감리원이 추가되었습니다.');
            }
            fetchAssignments();
            setShowAssignmentModal(false);
        } catch (error: any) {
            console.error('Failed to save assignment:', error);
            const message = error.response?.data?.message || '저장 중 오류가 발생했습니다.';
            toast.error(message);
        }
    };

    const handleAssignmentDelete = async (id: number) => {
        if (!window.confirm('이 감리원을 삭제하시겠습니까?')) return;

        try {
            await staffClient.delete(`/staffs/assignments/${id}`);
            toast.success('감리원이 삭제되었습니다.');
            fetchAssignments();
        } catch (error) {
            console.error('Failed to delete assignment:', error);
            toast.error('삭제 중 오류가 발생했습니다.');
        }
    };

    // Contract functions
    const fetchContracts = async () => {
        try {
            const response = await staffClient.get(`/contract?siteId=${siteId}`);
            setContracts(response.data);
        } catch (error) {
            console.error('Failed to fetch contracts:', error);
        }
    };

    const handleContractAdd = () => {
        setContractForm({
            contractRevision: 1,
            contractDate: '',
            status: '',
            workTypes: [],
            businessApprovalDate: '',
            biddingNoticeDate: '',
            remarks: ''
        });
        setContractFiles([]);
        setEditingContract(null);
        setShowContractModal(true);
    };

    const handleContractEdit = (contract: Contract) => {
        setContractForm({
            contractRevision: contract.contractRevision,
            contractDate: contract.contractDate,
            status: contract.status || '',
            workTypes: contract.workTypes || [],
            businessApprovalDate: contract.businessApprovalDate || '',
            biddingNoticeDate: contract.biddingNoticeDate || '',
            remarks: contract.remarks || ''
        });
        setContractFiles(contract.files || []);
        setEditingContract(contract);
        setShowContractModal(true);
    };

    const handleContractSave = async () => {
        setContractSaving(true);
        try {
            // 파일 업로드 처리
            const uploadedFiles = [];
            for (const file of contractFiles) {
                // 이미 업로드된 파일인 경우 (url이 있음)
                if (file.url) {
                    uploadedFiles.push({
                        name: file.name,
                        url: file.url,
                        type: file.type,
                        size: file.size,
                        hash: file.hash
                    });
                    continue;
                }
                // 새 파일 업로드
                const uploadUrl = await getS3UploadUrl(file);
                const uploadedUrl = await uploadS3File(file, uploadUrl);
                if (!uploadedUrl) {
                    toast.error('파일 업로드에 실패했습니다.');
                    setContractSaving(false);
                    return;
                }
                uploadedFiles.push({
                    name: file.name,
                    url: uploadedUrl,
                    type: file.type,
                    size: file.size,
                    hash: file.hash
                });
            }

            const data = {
                ...contractForm,
                siteId: Number(siteId),
                files: uploadedFiles
            };

            if (editingContract) {
                await staffClient.patch(`/contract/${editingContract.id}`, data);
                toast.success('계약이 수정되었습니다.');
            } else {
                await staffClient.post('/contract', data);
                toast.success('계약이 추가되었습니다.');
            }
            fetchContracts();
            setShowContractModal(false);
        } catch (error) {
            console.error('Failed to save contract:', error);
            toast.error('저장 중 오류가 발생했습니다.');
        } finally {
            setContractSaving(false);
        }
    };

    const handleContractDelete = async (id: number) => {
        if (!window.confirm('이 계약을 삭제하시겠습니까?')) return;
        
        try {
            await staffClient.delete(`/contract/${id}`);
            fetchContracts();
        } catch (error) {
            console.error('Failed to delete contract:', error);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    const positionLabels: Record<string, string> = {
        'CHIEF': '책임감리',
        'ASSISTANT': '보조감리',
        'NON_RESIDENT': '비상주',
        'ETC': '기타'
    };

    const assignmentColumns: Column<Assignment>[] = [
        {
            key: 'position',
            header: '구분',
            width: '90px',
            render: (value) => positionLabels[value] || value
        },
        {
            key: 'work_types',
            header: '공종',
            width: '140px',
            render: (value: string[]) => <WorkTypeBadges types={value} />
        },
        {
            key: 'staff',
            header: '이름',
            width: '120px',
            render: (_, row) => row.staff?.name ? <span className="font-medium">{row.staff.name}</span> : <span className="text-neutral-400">-</span>
        },
        {
            key: 'start_date',
            header: '시작일',
            width: '110px',
            render: (value) => formatDate(value)
        },
        {
            key: 'end_date',
            header: '종료일',
            width: '110px',
            render: (value) => formatDate(value)
        },
        {
            key: 'is_active',
            header: '상태',
            width: '80px',
            align: 'center',
            render: (value) => (
                <Badge variant={value ? 'success' : 'secondary'}>
                    {value ? '활성' : '비활성'}
                </Badge>
            )
        }
    ];

    const contractColumns: Column<Contract>[] = [
        {
            key: 'contractRevision',
            header: '차수',
            width: '60px',
            align: 'center',
            render: (value) => <span className="tabular-nums">{value}차</span>
        },
        { key: 'contractDate', header: '계약일', width: '100px', render: (value) => <span className="tabular-nums">{new Date(value).toLocaleDateString()}</span> },
        {
            key: 'status',
            header: '상태',
            width: '100px',
            render: (value) => value || <span className="text-neutral-400">-</span>
        },
        {
            key: 'workTypes',
            header: '공종',
            width: '120px',
            render: (value: string[] | undefined) => (
                <WorkTypeBadges types={(value || []).filter((t) => WORK_TYPE_BADGES[t])} />
            )
        },
        {
            key: 'businessApprovalDate',
            header: '사업시행인가일',
            width: '120px',
            render: (value) => formatDate(value)
        },
        {
            key: 'biddingNoticeDate',
            header: '입찰공고일',
            width: '100px',
            render: (value) => formatDate(value)
        },
        {
            key: 'files',
            header: '파일',
            width: '70px',
            align: 'center',
            render: (value: ContractFile[] | undefined) => {
                if (!value || value.length === 0) return <span className="text-neutral-400">-</span>;
                return <FileTooltip files={value} />;
            }
        },
        {
            key: 'remarks',
            header: '비고',
            width: '200px',
            render: (value) => value || <span className="text-neutral-400">-</span>
        }
    ];

    // 감리원 배치에서 계산한 감리 시작/종료일 — 현장정보에 직접 입력한 값이 없을 때 쓰이는 값
    const activeAssignments = assignments.filter(a => a.is_active);
    const assignmentStartDate = activeAssignments
        .map(a => a.start_date)
        .filter(Boolean)
        .reduce<string | null>((min, d) => (!min || d < min ? d : min), null);
    const assignmentEndDate = activeAssignments
        .map(a => a.end_date)
        .filter((d): d is string => Boolean(d))
        .reduce<string | null>((max, d) => (!max || d > max ? d : max), null);

    const manualStartDate = formData.supervisionReportDate || null;
    const manualFinishDate = formData.supervisionFinishDate || null;
    // 서버와 같은 규칙: 입력값 우선, 없으면 배치 계산값
    const effectiveStartDate = manualStartDate || assignmentStartDate;
    const effectiveEndDate = manualFinishDate || assignmentEndDate;

    const SourceBadge: React.FC<{ manual: boolean }> = ({ manual }) => (
        <Badge variant={manual ? 'default' : 'secondary'} className="ml-2 align-middle">
            {manual ? '현장정보 입력' : '배치 계산'}
        </Badge>
    );

    if (loading) {
        return (
            <PageContainer>
                <Loading text="현장 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="현장 수정"
                subtitle={site?.name}
                breadcrumb={[
                    { label: '관리자', path: '/main/home' },
                    { label: '현장 관리', path: '/admin/site-management' },
                    { label: '현장 수정' }
                ]}
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => navigate('/admin/site-management')}>
                            취소
                        </Button>
                        <Button variant="primary" onClick={handleSiteUpdate}>
                            저장
                        </Button>
                    </div>
                }
            />

            {/* Tabs Navigation and Content */}
            <Tabs
                tabs={[
                    {
                        id: 'basic',
                        label: '현장 정보',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        ),
                        content: (
                    <div className="space-y-4">
                        {/* 기본 정보 그룹 */}
                        <Section
                            title="기본 정보"
                            icon={
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            }
                        >
                            <div className={FORM_GRID}>
                                <TextField
                                    label="현장명"
                                    value={formData.name || ''}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                                <TextField
                                    label="위치"
                                    value={formData.location || ''}
                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                />
                                <SelectField
                                    label="구분"
                                    value={formData.category || 'SUPERVISION'}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value as SiteCategory })}
                                    placeholder=""
                                    options={SITE_CATEGORY_OPTIONS}
                                />
                                <SelectField
                                    label="상태"
                                    value={formData.type || 'ETC'}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    placeholder=""
                                    options={[
                                        { value: 'CONTRACT_COMPLETED', label: '계약완료' },
                                        { value: 'IN_PROGRESS', label: '진행중' },
                                        { value: 'COMPLETED', label: '준공' },
                                        { value: 'ETC', label: '기타' }
                                    ]}
                                />
                                <SelectField
                                    label="공개 여부"
                                    value={formData.visible ? 'true' : 'false'}
                                    onChange={(e) => setFormData({ ...formData, visible: e.target.value === 'true' })}
                                    placeholder=""
                                    options={[
                                        { value: 'true', label: '공개' },
                                        { value: 'false', label: '비공개' }
                                    ]}
                                />
                            </div>
                        </Section>

                        {/* 감리 일정 · 공종 — 현장정보에서 직접 입력 (감리원 배치 min/max에 의존하지 않음) */}
                        <Section
                            title="감리 정보"
                            description="감리 시작·종료일은 여기에 입력한 값이 우선 적용됩니다. 비워 두면 활성 감리원의 배치 기간(최소~최대)으로 자동 계산됩니다."
                            icon={
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            }
                        >
                            <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-3">
                                <TextField
                                    label="감리 시작신고일"
                                    type="date"
                                    value={formData.supervisionReportDate || ''}
                                    onChange={(e) => setFormData({ ...formData, supervisionReportDate: e.target.value })}
                                    hint={
                                        manualStartDate
                                            ? '이 값이 감리 시작일로 적용됩니다'
                                            : assignmentStartDate
                                                ? `비워두면 배치 계산값 ${formatDateText(assignmentStartDate)} 적용`
                                                : '비워두면 감리원 배치에서 계산'
                                    }
                                />
                                <TextField
                                    label="감리원 투입일"
                                    type="date"
                                    value={formData.supervisionDeployDate || ''}
                                    onChange={(e) => setFormData({ ...formData, supervisionDeployDate: e.target.value })}
                                    hint="실제 감리원이 현장에 투입된 날"
                                />
                                <TextField
                                    label="감리 종료일"
                                    type="date"
                                    value={formData.supervisionFinishDate || ''}
                                    onChange={(e) => setFormData({ ...formData, supervisionFinishDate: e.target.value })}
                                    hint={
                                        manualFinishDate
                                            ? '이 값이 감리 종료일로 적용됩니다'
                                            : assignmentEndDate
                                                ? `비워두면 배치 계산값 ${formatDateText(assignmentEndDate)} 적용`
                                                : '비워두면 감리원 배치에서 계산'
                                    }
                                />
                                <ToggleGroup
                                    label="공종"
                                    multiple
                                    options={SITE_WORK_TYPE_OPTIONS}
                                    value={formData.workTypes || []}
                                    onChange={(next) => setFormData({ ...formData, workTypes: next })}
                                />
                            </div>
                        </Section>

                        {/* 공사 정보 그룹 */}
                        <Section
                            title="공사 정보"
                            icon={
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            }
                        >
                            <div className={FORM_GRID}>
                                <TextField
                                    label="공사 착공일"
                                    type="date"
                                    value={formData.constructionStartDate || ''}
                                    onChange={(e) => setFormData({ ...formData, constructionStartDate: e.target.value })}
                                />
                                <TextField
                                    label="공사 준공일"
                                    type="date"
                                    value={formData.constructionEndDate || ''}
                                    onChange={(e) => setFormData({ ...formData, constructionEndDate: e.target.value })}
                                />
                                <TextField
                                    label="시공사"
                                    value={formData.contractorName || ''}
                                    onChange={(e) => setFormData({ ...formData, contractorName: e.target.value })}
                                />
                                <TextField
                                    label="진행률 (%)"
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={formData.progressRate || ''}
                                    onChange={(e) => setFormData({ ...formData, progressRate: e.target.value })}
                                />
                            </div>
                        </Section>

                        {/* 건축 상세 정보 그룹 */}
                        <Section
                            title="건축 상세"
                            icon={
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h2m0 0h2m0 0h2a2 2 0 002-2V7a2 2 0 00-2-2h-2m0 0V3m0 0h2a2 2 0 012 2v2M7 7h2v2H7V7z" />
                                </svg>
                            }
                        >
                            <div className={FORM_GRID}>
                                <TextField
                                    label="세대수"
                                    value={formData.numberOfUnits || ''}
                                    onChange={(e) => setFormData({ ...formData, numberOfUnits: e.target.value })}
                                    placeholder="예: 1,200세대"
                                />
                                <TextField
                                    label="연면적"
                                    value={formData.totalFloorArea || ''}
                                    onChange={(e) => setFormData({ ...formData, totalFloorArea: e.target.value })}
                                    placeholder="예: 150,000㎡"
                                />
                                <TextField
                                    label="건축규모"
                                    value={formData.buildingScale || ''}
                                    onChange={(e) => setFormData({ ...formData, buildingScale: e.target.value })}
                                    placeholder="예: 지하 3층, 지상 25층"
                                />
                            </div>
                        </Section>
                    </div>
                        )
                    },
                    {
                        id: 'supervisors',
                        label: '감리원 관리',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        ),
                        content: (
                    <div className="space-y-4">
                        {/* 적용 중인 감리 일정 — 현장 정보 탭 입력값이 있으면 그 값, 없으면 배치 계산값 */}
                        <Section
                            title="적용 중인 감리 일정"
                            description="현장 정보 탭에 입력한 값이 있으면 그 값을, 없으면 활성 감리원의 배치 기간(최소~최대)을 사용합니다. 수정은 현장 정보 탭에서 합니다."
                            icon={
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            }
                        >
                            <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-3">
                                <ReadonlyField
                                    label={<>감리 시작일<SourceBadge manual={!!manualStartDate} /></>}
                                >
                                    {formatDateText(effectiveStartDate) || <span className="text-neutral-400">-</span>}
                                </ReadonlyField>
                                <ReadonlyField
                                    label={
                                        <>
                                            {formData.type === 'COMPLETED' ? '감리 종료일 (신고 완료)' : '감리 종료일 (예정)'}
                                            <SourceBadge manual={!!manualFinishDate} />
                                        </>
                                    }
                                >
                                    {formatDateText(effectiveEndDate) || <span className="text-neutral-400">-</span>}
                                </ReadonlyField>
                                <ReadonlyField label="감리원 투입일">
                                    {formatDateText(formData.supervisionDeployDate) || <span className="text-neutral-400">-</span>}
                                </ReadonlyField>
                            </div>
                            <div className="mt-3 max-w-md">
                                <ReadonlyField label="공종">
                                    <WorkTypeBadges types={formData.workTypes} />
                                </ReadonlyField>
                            </div>
                        </Section>

                        {/* 공종별 감리원 표 */}
                        <TableContainer title="공종별 감리원">
                            <Table
                                columns={[
                                    {
                                        key: 'workType',
                                        header: '공종',
                                        width: '100px',
                                        render: (value: string) => <WorkTypeBadges types={[value]} />
                                    },
                                    {
                                        key: 'chiefs',
                                        header: '책임',
                                        render: (value: Assignment[]) => value.length > 0 ? (
                                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                                                {value.map(a => <span key={a.id}>{a.staff?.name}</span>)}
                                            </div>
                                        ) : <span className="text-neutral-400">-</span>
                                    },
                                    {
                                        key: 'assistants',
                                        header: '보조',
                                        render: (value: Assignment[]) => value.length > 0 ? (
                                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                                                {value.map(a => <span key={a.id}>{a.staff?.name}</span>)}
                                            </div>
                                        ) : <span className="text-neutral-400">-</span>
                                    }
                                ]}
                                data={['ELECTRIC', 'TELECOM', 'FIRE'].map((workType) => ({
                                    workType,
                                    chiefs: assignments.filter(a =>
                                        a.position === 'CHIEF' && a.is_active && a.work_types?.includes(workType)
                                    ),
                                    assistants: assignments.filter(a =>
                                        a.position === 'ASSISTANT' && a.is_active && a.work_types?.includes(workType)
                                    )
                                }))}
                            />
                        </TableContainer>

                        {/* 감리원 목록 */}
                        <TableContainer
                            title="감리원 목록"
                            resultCount={assignments.length}
                            actions={
                                <Button size="small" variant="outline" icon={<Plus />} onClick={handleAssignmentAdd}>
                                    감리원 추가
                                </Button>
                            }
                        >
                            <Table
                                columns={assignmentColumns}
                                data={assignments}
                                actions={(assignment) => (
                                    <>
                                        <Button size="small" variant="outline" onClick={() => handleAssignmentEdit(assignment)}>
                                            수정
                                        </Button>
                                        <Button size="small" variant="danger-outline" onClick={() => handleAssignmentDelete(assignment.id)}>
                                            삭제
                                        </Button>
                                    </>
                                )}
                            />
                        </TableContainer>
                    </div>
                        )
                    },
                    {
                        id: 'contracts',
                        label: '계약 관리',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        ),
                        content: (
                    <TableContainer
                        title="계약 정보"
                        resultCount={contracts.length}
                        actions={
                            <Button size="small" variant="outline" icon={<Plus />} onClick={handleContractAdd}>
                                계약 추가
                            </Button>
                        }
                    >
                        <Table
                            columns={contractColumns}
                            data={contracts}
                            actions={(contract) => (
                                <>
                                    <Button size="small" variant="outline" onClick={() => handleContractEdit(contract)}>
                                        수정
                                    </Button>
                                    <Button size="small" variant="danger-outline" onClick={() => handleContractDelete(contract.id)}>
                                        삭제
                                    </Button>
                                </>
                            )}
                        />
                    </TableContainer>
                        )
                    },
                    {
                        id: 'documents',
                        label: '자료실 현황',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                        ),
                        content: (
                    <Section
                        title="자료실 문서 현황"
                        description="문서 게시판별 카테고리 문서 수"
                        icon={
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                        }
                        actions={
                            <Button
                                size="small"
                                variant="outline"
                                icon={<RefreshCw />}
                                onClick={() => {
                                    if (site) {
                                        fetchDocumentBoards(site);
                                    }
                                }}
                            >
                                새로고침
                            </Button>
                        }
                    >
                        {documentBoards.length > 0 ? (
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                                {documentBoards.map((board) => {
                                    const boardCategories = board.categories || [];
                                    const documentCategoryName = documentCategories[board.document_category || ''] || board.document_category || '기타';
                                    const total = boardCategories.reduce((sum: number, cat: any) =>
                                        sum + (documentCounts[`${board.id}-${cat.id}`] || 0), 0
                                    );

                                    return (
                                        <div
                                            key={board.id}
                                            className="overflow-hidden rounded border border-border [&_tbody_tr:last-child]:border-b-0"
                                        >
                                            <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                                                <h3 className="text-base font-semibold text-foreground">{documentCategoryName}</h3>
                                                <span className="text-xs text-muted-foreground tabular-nums">
                                                    합계 <span className="font-medium text-foreground">{total}</span>건
                                                </span>
                                            </div>
                                            <Table
                                                columns={[
                                                    { key: 'name', header: '카테고리' },
                                                    {
                                                        key: 'id',
                                                        header: '문서 수',
                                                        width: '80px',
                                                        align: 'right',
                                                        render: (_: any, category: any) => {
                                                            const count = documentCounts[`${board.id}-${category.id}`] || 0;
                                                            return (
                                                                <span className={cn('tabular-nums', count > 0 ? 'font-medium text-foreground' : 'text-neutral-400')}>
                                                                    {count}
                                                                </span>
                                                            );
                                                        }
                                                    }
                                                ]}
                                                data={[...boardCategories].sort((a: any, b: any) => a.order - b.order)}
                                                emptyMessage="카테고리가 없습니다"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="py-12 text-center text-sm text-muted-foreground">문서 게시판이 없습니다</p>
                        )}
                    </Section>
                        )
                    },
                    {
                        id: 'categories',
                        label: '게시판 카테고리',
                        icon: (
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                        ),
                        content: (
                    <Section
                        title="게시판 카테고리"
                        icon={
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                        }
                        description={
                            selectedBoard ? (
                                <>
                                    연결 게시판{' '}
                                    <button
                                        type="button"
                                        className="font-medium text-info hover:underline"
                                        onClick={() => navigate(`/board/${selectedBoard.id}`)}
                                    >
                                        {selectedBoard.name}
                                    </button>
                                    {' '}· 드래그로 순서 변경, 이름을 클릭해 수정한 뒤 저장하세요.
                                </>
                            ) : undefined
                        }
                        actions={
                            selectedBoard ? (
                                <Button
                                    size="small"
                                    variant="primary"
                                    onClick={handleCategoriesSave}
                                    disabled={savingCategories}
                                >
                                    {savingCategories ? '저장 중...' : '저장'}
                                </Button>
                            ) : undefined
                        }
                    >
                        {selectedBoard ? (
                            <CategoryManager
                                categories={boardCategories}
                                onChange={handleCategoriesChange}
                            />
                        ) : (
                            <div className="py-12 text-center text-sm text-muted-foreground">
                                <p className="mb-1">이 현장에 연결된 게시판이 없습니다.</p>
                                <p>먼저 게시판을 생성해주세요.</p>
                            </div>
                        )}
                    </Section>
                        )
                    }
                ]}
                defaultTab="basic"
                variant="default"
            />


            {/* Assignment Modal */}
            <Modal
                isOpen={showAssignmentModal}
                onClose={() => setShowAssignmentModal(false)}
                title={editingAssignment ? '감리원 수정' : '감리원 추가'}
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowAssignmentModal(false)}>
                            취소
                        </Button>
                        <Button variant="primary" onClick={handleAssignmentSave}>
                            저장
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <SearchSelect
                        label="직원 선택"
                        value={assignmentForm.staff_id || ''}
                        onChange={(value) => setAssignmentForm({ ...assignmentForm, staff_id: Number(value) })}
                        options={staffList.map(staff => ({
                            value: staff.id,
                            label: staff.name,
                            subLabel: staff.user_id
                        }))}
                        placeholder="직원을 선택하세요"
                        searchPlaceholder="이름 또는 아이디로 검색..."
                        emptyMessage="일치하는 직원이 없습니다"
                    />
                    <SelectField
                        label="구분"
                        value={assignmentForm.position || 'CHIEF'}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, position: e.target.value as 'CHIEF' | 'ASSISTANT' | 'NON_RESIDENT' | 'ETC' })}
                        options={[
                            { value: 'CHIEF', label: '책임감리' },
                            { value: 'ASSISTANT', label: '보조감리' },
                            { value: 'NON_RESIDENT', label: '비상주' },
                            { value: 'ETC', label: '기타' }
                        ]}
                    />
                    <ToggleGroup
                        label="공종"
                        multiple
                        options={[
                            { value: 'ELECTRIC', label: '전기' },
                            { value: 'TELECOM', label: '통신' },
                            { value: 'FIRE', label: '소방' }
                        ]}
                        value={assignmentForm.work_types || []}
                        onChange={(next) => setAssignmentForm({ ...assignmentForm, work_types: next })}
                    />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextField
                            label="시작일"
                            type="date"
                            value={assignmentForm.start_date || ''}
                            onChange={(e) => setAssignmentForm({ ...assignmentForm, start_date: e.target.value })}
                        />
                        <TextField
                            label="종료일"
                            type="date"
                            value={assignmentForm.end_date || ''}
                            onChange={(e) => setAssignmentForm({ ...assignmentForm, end_date: e.target.value })}
                        />
                    </div>
                    <SelectField
                        label="상태"
                        value={assignmentForm.is_active ? 'true' : 'false'}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, is_active: e.target.value === 'true' })}
                        options={[
                            { value: 'true', label: '활성' },
                            { value: 'false', label: '비활성' }
                        ]}
                    />
                </div>
            </Modal>

            <Modal
                isOpen={showContractModal}
                onClose={() => setShowContractModal(false)}
                title={editingContract ? '계약 수정' : '계약 추가'}
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowContractModal(false)} disabled={contractSaving}>
                            취소
                        </Button>
                        <Button variant="primary" onClick={handleContractSave} disabled={contractSaving}>
                            {contractSaving ? '저장 중...' : '저장'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextField
                            label="계약 변경 차수"
                            type="number"
                            min="1"
                            value={contractForm.contractRevision}
                            onChange={(e) => setContractForm({ ...contractForm, contractRevision: parseInt(e.target.value) || 1 })}
                            placeholder="1, 2, 3..."
                        />
                        <TextField
                            label="계약일"
                            type="date"
                            value={contractForm.contractDate}
                            onChange={(e) => setContractForm({ ...contractForm, contractDate: e.target.value })}
                        />
                    </div>
                    <TextField
                        label="상태"
                        value={contractForm.status}
                        onChange={(e) => setContractForm({ ...contractForm, status: e.target.value })}
                        placeholder="상태를 입력하세요"
                    />
                    <ToggleGroup
                        label="공종"
                        multiple
                        options={[
                            { value: 'ELECTRIC', label: '전기' },
                            { value: 'NETWORK', label: '통신' },
                            { value: 'FIRE', label: '소방' }
                        ]}
                        value={contractForm.workTypes || []}
                        onChange={(next) => setContractForm({ ...contractForm, workTypes: next })}
                    />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextField
                            label="사업시행인가일"
                            type="date"
                            value={contractForm.businessApprovalDate}
                            onChange={(e) => setContractForm({ ...contractForm, businessApprovalDate: e.target.value })}
                        />
                        <TextField
                            label="입찰공고일"
                            type="date"
                            value={contractForm.biddingNoticeDate}
                            onChange={(e) => setContractForm({ ...contractForm, biddingNoticeDate: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-neutral-700">첨부파일</span>
                        <MultiFileUploader
                            files={contractFiles}
                            onFilesSelected={setContractFiles}
                        />
                    </div>
                    <TextField
                        label="비고"
                        value={contractForm.remarks}
                        onChange={(e) => setContractForm({ ...contractForm, remarks: e.target.value })}
                        placeholder="추가 정보를 입력하세요"
                    />
                </div>
            </Modal>
        </PageContainer>
    );
};

export default SiteEdit;